import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { UserRole } from '@/types/feedback';

export type Profile = Database['public']['Tables']['profiles']['Row'];

type Organization = {
  id: string;
  name: string;
  plan: string;
  owner_id: string | null;
  is_active: boolean;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  organization: Organization | null;
  isLoading: boolean;
  signUp: (email: string, password: string, options: { 
    full_name?: string, 
    role: UserRole,
    organization?: string,
    website_url?: string
  }) => Promise<{ error: any | null }>;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 500);
        } else {
          setProfile(null);
          setOrganization(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Existing session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    console.log('Fetching profile for user:', userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
      return;
    }

    if (!data) {
      console.warn('No profile found for user:', userId);
      await syncUserProfile(userId);
    } else {
      console.log('Profile found:', data.id);
      setProfile(data);
      
      if (data.corporate_group_id) {
        fetchOrganization(data.corporate_group_id);
      } else {
        setOrganization(null);
      }
    }
  };
  
  const fetchOrganization = async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();
        
      if (error) {
        console.error('Error fetching organization:', error);
        setOrganization(null);
        return;
      }
      
      setOrganization(data);
    } catch (error) {
      console.error('Exception during organization fetch:', error);
      setOrganization(null);
    }
  };
  
  const syncUserProfile = async (userId: string) => {
    try {
      console.log('Attempting to sync profile for user:', userId);
      const { error } = await supabase.rpc('sync_users_and_profiles');
      
      if (error) {
        console.error('Error syncing user profile:', error);
        return;
      }
      
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (data) {
        console.log('Profile created by sync:', data.id);
        setProfile(data);
        
        if (data.corporate_group_id) {
          fetchOrganization(data.corporate_group_id);
        }
      } else {
        console.warn('Profile still missing after sync for user:', userId);
      }
    } catch (error) {
      console.error('Exception during profile sync:', error);
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    await fetchProfile(user.id);
  };

  const signUp = async (
    email: string, 
    password: string, 
    options: { 
      full_name?: string, 
      role: UserRole,
      organization?: string,
      website_url?: string
    }
  ) => {
    try {
      console.log("Début inscription avec options:", options);
      
      const { data, error: functionError } = await supabase.functions.invoke('create-user', {
        body: {
          email,
          password,
          userData: {
            full_name: options.full_name || '',
            role: options.role,
            organization: options.organization || '',
            website_url: options.website_url || '',
            is_active: true
          }
        }
      });
      
      if (functionError) {
        console.error("Erreur d'inscription via fonction:", functionError);
        return { error: functionError };
      }
      
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (signInError) {
        console.error("Erreur lors de la connexion après inscription:", signInError);
        return { error: signInError };
      }
      
      console.log("Inscription et connexion réussies");
      return { error: null };
    } catch (error) {
      console.error("Exception lors de l'inscription:", error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (!error) {
        await supabase.auth.refreshSession();
      }
      
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    session,
    user,
    profile,
    organization,
    isLoading,
    signUp,
    signIn,
    signOut,
    refreshProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
